import * as React from "react";
import { IconButton, Stack, Typography, useTheme } from "@mui/material";
import { getOverrideModeByValue, OVERRIDE_TYPES } from "../../enums/override_enum";
import { asKw, formatRecentDate, timeAgo } from "../../scripts/utils";
import { useDevice, useSiteData } from "../../hooks/use_site";
import { useOverrides } from "../../hooks/use_overrides";
import { CollapsableCard } from "../general/collapsable_card";
import CreateIcon from "@mui/icons-material/Create";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { useModal } from "../../context/modal_provider";

function getValue(data) {
  return data.type === OVERRIDE_TYPES.FRACTION.value ? data.value * 100 + "%" : asKw(data.value) + "kW";
}

export function OverridesCard({ overrides, isLoading, error, canEdit, startCollapsed = false, ...props }) {
  const { openOverrideModal } = useModal();
  const { data: siteData } = useSiteData();

  return (
    <CollapsableCard
      startCollapsed={startCollapsed || !overrides || overrides.length == 0}
      title={`Overrides ${overrides ? "(" + overrides.length + ")" : ""}`}
      {...props}
      rightAdornment={canEdit ? <OpenOverridesButton onClick={() => openOverrideModal({ siteData })} /> : null}
    >
      {!error ? (
        overrides &&
        overrides.map((el, idx) => (
          <OverrideItem key={idx} override={el} onClick={canEdit ? () => openOverrideModal({ siteData }) : null} />
        ))
      ) : (
        <Typography variant="body" sx={{ ml: 6 }}>
          Error loading overrides
        </Typography>
      )}
    </CollapsableCard>
  );
}

function OverrideItem({ override, onClick }) {
  const overrideModeFromEnum = getOverrideModeByValue(override.override);
  const theme = useTheme();

  const device = useDevice(override.device_id);

  const formatUnknownOverride = (name) => {
    const formatted = name.replace(/_/g, " ").toLowerCase();
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  };

  const overrideMode = overrideModeFromEnum || {
    icon: ({ ...props }) => <AutorenewIcon {...props} />,
    label: formatUnknownOverride(override.override),
  };

  return (
    <Stack
      flexDirection="row"
      alignItems="center"
      spacing={1}
      onClick={onClick}
      sx={{ cursor: onClick ? "pointer" : "default" }}
    >
      <overrideMode.icon
        style={{ minWidth: "30px", fontSize: "30px", marginLeft: "10px", color: theme.palette.primary.main }}
      />
      <Stack
        flexDirection="column"
        sx={{
          paddingLeft: 2,
          textAlign: "left",
          opacity: override.enabled ? 1 : 0.7,
          textDecoration: override.enabled ? "none" : "line-through",
        }}
      >
        <Typography variant="body2" fontWeight="bold">
          {overrideMode.label} to {getValue(override)} {device ? " on " + device.name : null}{" "}
          {override.creator ? "by " + override.creator : null}
        </Typography>
        <Typography variant="caption">
          <span style={{ color: "#aaa" }}>{timeAgo(new Date(1000 * override.creationdate))}</span> {"\u2022"}{" "}
          {override.valid_from_utc ? "From " + formatRecentDate(new Date(1000 * override.valid_from_utc)) : ""}{" "}
          {override.valid_to_utc ? "until " + formatRecentDate(new Date(1000 * override.valid_to_utc)) : ""}
        </Typography>
        <Typography variant="caption" color={"#aaa"} sx={{ textAlign: "left" }}></Typography>
      </Stack>
    </Stack>
  );
}

function OpenOverridesButton({ ...props }) {
  return (
    <IconButton sx={{ width: "30px", height: "30px", padding: 0 }} {...props}>
      <CreateIcon style={{ color: "#777" }} />
    </IconButton>
  );
}
